import { defineMessages } from 'react-intl';

export const messages = defineMessages({
  addStage: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.addStage',
    defaultMessage: 'Add Stage',
  },
  backgroundCheckerStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.backgroundCheckerStageDescription',
    defaultMessage:
      'A background check stage requires an integration with a preferred vendor. Once the integration is set up, the stage is broken up into two components, one of which is required:{br}{br} Automated Message - Applicants will receive a message including a link to a background check form. Upon consenting to the background check, you can either automatically run the background check or not.{br}{br} Trigger Message - After an applicant has not consented to a background check for a set amount of days, auto-send a Trigger Message (you can set multiple).',
  },
  cancel: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.cancel',
    defaultMessage: 'Cancel',
  },
  customStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.customStageDescription',
    defaultMessage:
      'After an applicant lands on a manual stage, you have the option of setting an Automated Message to send to the applicant. If no Automated Message is set, nothing happens.',
  },
  dataCollectionStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.dataCollectionStageDescription',
    defaultMessage:
      'Upon landing on a Data Collection stage, applicants will receive a message with a link asking them to complete a form (and upload files/answer questions). This information will be securely appended to their applicant profile.',
  },
  // Rendered for DocumentSignatureStage only — the "Document Signing Stage
  // (Docusign)" option, which workflow_helper.rb offers solely to accounts with a
  // DocuSign integration. Its Fountain sibling, DocumentSigningStage, maps to
  // `null` in StageDescriptionTextMap and shows no description at all.
  //
  // AXHE-4226: the claim used to be made of "Document Signing stages" as a family
  // and covered HelloSign as well — both leftovers from the v1 picker, where one
  // generic option covered every provider. The family reading is now false: a
  // Fountain document signing stage can carry an offer letter template, which is
  // sent by a recruiter rather than upon landing. Scoped to the stage the
  // sentence is actually shown for, the landing behaviour it describes is true —
  // offer letter templates cannot be attached to a DocuSign stage
  // (`stage.type === 'DocumentSigningStage'` guards every offer letter path in
  // <Documents>), so this copy must not mention them either.
  //
  // A FRESH id, retiring `…NewStageModal.documentSignatureStageDescription`
  // rather than rewording it in place. react-intl prefers a catalog hit over a
  // changed `defaultMessage`, and that id is translated in all 76 non-English
  // catalogs — every one of them carrying the old sentence, including the two
  // things this change exists to remove (the family framing and HelloSign). So a
  // non-English admin would have kept reading the false version indefinitely.
  // Same trade, same package, same reasoning as #3447's `pageTitle` /
  // `pageDescription` retirement (germs12 review on #3547).
  docusignStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.docusignStageDescription',
    defaultMessage:
      'Once an integration is complete with your DocuSign API account, this stage automatically sends a DocuSign template to applicants to sign upon landing.',
    description:
      'Describes what the DocuSign document signing stage type does, shown when an admin picks it while creating a stage',
  },
  jobSelectorStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.jobSelectorStageDescription',
    defaultMessage:
      'Upon landing on a Job Selector stage, applicants will have the choice to select one opening from a list of active, hiring openings that match conditions configured on this stage. Once they select an opening, they will self transition to the first stage of that opening, or to the next stage if they select the opening they are currently in.',
  },
  jobSwitcherStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.jobSwitcherStageDescription',
    defaultMessage:
      'Job Switcher stages appear to applicants when an opening is inactive. Upon landing, applicants will have the choice to select one opening from a list of active, hiring openings that use the same workflow, and match conditions configured on this stage. Once they select an opening, they will self transition to the next stage in that opening, maintaining previous progress. If an opening is active, applicants will always skip over the Job Switcher stage. Requires 1:Many Workflows to function.',
  },
  header: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.header',
    defaultMessage: 'Create New Stage',
  },
  reviewStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.reviewStageDescription',
    defaultMessage:
      "A Review Stage is a decision point where a hiring manager's manual action is required to advance applicants, and where data can be appended to an applicant by the hiring manager",
  },
  schedulerStageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.schedulerStageDescription',
    defaultMessage:
      'A scheduler stage is broken up into three components, two of which are required: {br}{br}Scheduler Message - When an applicant lands, Scheduler Message is sent containing available sessions created in the Fountain Calendar.{br}{br} Confirmation Message - Once an applicant books a session from the Scheduler Message, a Confirmation Message is sent.',
  },
  schedulerV2StageDescription: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.schedulerV2StageDescription',
    defaultMessage:
      'A scheduler stage is broken up into three components, two of which are required: {br}{br}Scheduler Message - When an applicant lands, Scheduler Message is sent containing a link to book an interview.{br}{br} Confirmation Message - Once an applicant books a session from the Scheduler Message, a Confirmation Message is sent.',
  },
  selectAStage: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.selectAStage',
    defaultMessage: 'Select a stage',
  },
  selectAStageType: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.selectAStageType',
    defaultMessage: 'Select a stage type',
  },
  selectAState: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.selectAState',
    defaultMessage: 'Select a state',
  },
  stageLoadError: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.stageLoadError',
    defaultMessage: 'Failed to load available stages, please try again.',
  },
  stagePlacement: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.stagePlacement',
    defaultMessage: 'Stage Placement',
  },
  stageRequired: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.stageRequired',
    defaultMessage: 'Add a stage title',
  },
  stageSuccess: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.stageSuccess',
    defaultMessage: 'Stage created successfully',
  },
  stageType: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.stageType',
    defaultMessage: 'Stage Type',
  },
  state: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.state',
    defaultMessage: 'State',
  },
  title: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.title',
    defaultMessage: 'Title',
  },
  w4StateFormNotice: {
    id: 'app.containers.WorkflowEditor.components.StageColumnHeader.NewStageModal.w4StateFormNotice',
    defaultMessage:
      'Note that you cannot create a W-4 State form for the following states: ND, NM, UT. These states uses the W-4 Federal form for Employee Withholding and you should select this stage type instead.',
  },
});
